import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {EditorContent} from '@tiptap/react';
import {useSelector} from 'react-redux';
import ElementButtonComponent from '../../../../components/elements/button/button';
import FormElementComponent from '../../../../components/elements/form/formElement';
import ElementRectangleComponent from '../../../../components/elements/geometry/rectangle';
import LineElementV2 from '../../../../components/elements/geometry/LineElementV2';
import ElementImageComponent from '../../../../components/elements/image/image';
import ElementTextInput from '../../../../components/elements/input/textInput';
import ElementHtmlComponent from '../../../../components/elements/text/html';
import ImageMarkupQuestionElement from '../../../../components/elements/question/imageMarkup/ImageMarkupQuestionElement';
import LikertScaleQuestionElement from '../../../../components/elements/question/likertScale/LikertScaleQuestionElement';
import MatchPairQuestionElement from '../../../../components/elements/question/matchPair/MatchPairQuestionElement';
import MultiselectQuestionElement from '../../../../components/elements/question/multiselect/MultiselectQuestionElement';
import PhotoAnswerQuestionElement from '../../../../components/elements/question/photoAnswer/PhotoAnswerQuestionElement';
import QuestionElement from '../../../../components/elements/question/question/QuestionElement';
import SequenceQuestionElement from '../../../../components/elements/question/sequence/SequenceQuestionElement';
import YesNoQuestionElementV2 from '../../../../components/elements/question/yesNoQuestion/YesNoQuestionElementV2';
import {PresScaleProvider} from '../../../../context/PresScaleContext';
import {ELEMENT} from '../../../../incase-domain/elements/base/ElementTypes';
import {IElement} from '../../../../incase-domain/elements';
import {IElementButton} from '../../../../incase-domain/elements/button';
import {IElementForm} from '../../../../incase-domain/elements/form';
import {IElementHtml} from '../../../../incase-domain/elements/html/html';
import {IElementImage} from '../../../../incase-domain/elements/image';
import {IElementTextInput} from '../../../../incase-domain/elements/textInput';
import {IElementLineV2} from '../../../../incase-domain/elements/shapes/lineV2';
import {IElementRectangle} from '../../../../incase-domain/elements/shapes/rectangle';
import {IElementImageMarkup} from '../../../../incase-domain/elements/question/imageMarkup';
import {IElementLikertScale} from '../../../../incase-domain/elements/question/likertScale';
import {IElementMultiSelect} from '../../../../incase-domain/elements/question/multiSelect';
import {IElementPhotoAnswerQuestion} from '../../../../incase-domain/elements/question/photoAnswerQuestion';
import {IElementQuestion} from '../../../../incase-domain/elements/question/question';
import {IElementQuestionMatchPair} from '../../../../incase-domain/elements/question/questionMatchPair';
import {IElementSequenceQuestion} from '../../../../incase-domain/elements/question/questionSequence';
import {IElementQuestionYesNoV2} from '../../../../incase-domain/elements/question/questionYesNoV2';
import {IPresentationTheme} from '../../../../incase-domain/presentationTheme';
import {AppState} from '../../../../store/reducer';
import {editorSelector} from '../../../../store/selectors/tiptapEditor.selectors';
import {getBorderStyle} from '../../../../utils/style.utils';
import {buildThemeControlElement, buildThemePreviewElement} from './buildThemePreviewElementByType';
import {getThemeNavEntry, PresentationThemeNavItem} from './themeNav';

export const THEME_TEXT_PREVIEW_ID = 'presentation-theme-text-preview';

const QUESTION_PREVIEW_RENDERERS: Partial<Record<ELEMENT, (element: IElement) => JSX.Element>> = {
    [ELEMENT.QUESTION]: (element) => <QuestionElement element={element as IElementQuestion} />,
    [ELEMENT.YES_NO_QUESTION_V2]: (element) => <YesNoQuestionElementV2 element={element as IElementQuestionYesNoV2} />,
    [ELEMENT.MULTI_SELECT]: (element) => <MultiselectQuestionElement element={element as IElementMultiSelect} />,
    [ELEMENT.PHOTO_ANSWER_QUESTION]: (element) => <PhotoAnswerQuestionElement element={element as IElementPhotoAnswerQuestion} />,
    [ELEMENT.SEQUENCE_QUESTION]: (element) => <SequenceQuestionElement element={element as IElementSequenceQuestion} />,
    [ELEMENT.MATCH_PAIR_QUESTION]: (element) => <MatchPairQuestionElement element={element as IElementQuestionMatchPair} />,
    [ELEMENT.LIKERT_SCALE]: (element) => <LikertScaleQuestionElement element={element as IElementLikertScale} />,
    [ELEMENT.IMAGE_MARKUP]: (element) => <ImageMarkupQuestionElement element={element as IElementImageMarkup} />,
};

function previewFrameStyle(element: IElement): React.CSSProperties {
    if (element.type === ELEMENT.LINE_V2) return {width: '100%', height: '4em'};
    if (element.type === ELEMENT.RECTANGLE) return {width: '40%', height: '8em', margin: '0 auto'};
    if (element.type === ELEMENT.IMAGE) return {width: '60%', height: '8em', margin: '0 auto'};
    return {width: '100%'};
}

function renderThemeElement(element: IElement): JSX.Element | null {
    switch (element.type) {
        case ELEMENT.HTML:
            return <ElementHtmlComponent element={element as IElementHtml} />;
        case ELEMENT.IMAGE:
            return <ElementImageComponent element={element as IElementImage} />;
        case ELEMENT.BUTTON:
            return <ElementButtonComponent element={element as IElementButton} />;
        case ELEMENT.TEXT_INPUT:
            return <ElementTextInput element={element as IElementTextInput} />;
        case ELEMENT.FORM:
            return <FormElementComponent element={element as IElementForm} />;
        case ELEMENT.RECTANGLE:
            return <ElementRectangleComponent element={element as IElementRectangle} />;
        case ELEMENT.LINE_V2:
            return <LineElementV2 element={element as IElementLineV2} />;
        default:
            return null;
    }
}

interface ThemePreviewPanelProps {
    activeItem: PresentationThemeNavItem;
    presentationTheme: IPresentationTheme;
}

function ThemePreviewPanel({activeItem, presentationTheme}: ThemePreviewPanelProps): JSX.Element {
    const {t} = useTranslation();
    const entry = getThemeNavEntry(activeItem);
    const elementType = entry.element;
    const textTheme = presentationTheme.elementStyles?.textElement;
    const textEditor = useSelector((state: AppState) => editorSelector(state, THEME_TEXT_PREVIEW_ID));
    const questionPreviewElement = useMemo(
        () => (entry.group === 'question' && elementType ? buildThemePreviewElement(elementType, presentationTheme) : null),
        [elementType, entry.group, presentationTheme]
    );
    const elementPreview = useMemo(() => {
        if (!elementType || entry.group === 'question' || entry.group === 'text' || entry.group === 'general') return null;
        return buildThemeControlElement(elementType, presentationTheme);
    }, [elementType, entry.group, presentationTheme]);
    const renderQuestionPreview = QUESTION_PREVIEW_RENDERERS[questionPreviewElement?.type ?? ELEMENT.QUESTION];

    return (
        <PresScaleProvider>
            <div className="c-themePreview">
                <div className="c-themePreview__label">{t('presentationThemesPage.preview')}</div>
                <div className="c-themePreview__canvas">
                    <div>
                        {entry.group === 'text' && (
                            <div
                                className="c-themePreview__text c-tiptap c-textElement ql-editor"
                                style={{
                                    color: textTheme?.color || undefined,
                                    backgroundColor: textTheme?.backgroundColor || '#fff',
                                    borderRadius: getBorderStyle(textTheme?.borderRadius) || undefined,
                                    fontSize: textTheme?.fontSize?.value ? `${textTheme.fontSize.value}em` : '1em',
                                }}
                            >
                                {textEditor ? <EditorContent editor={textEditor} /> : t('presentationThemesPage.text.textElement')}
                            </div>
                        )}

                        {entry.group === 'question' && questionPreviewElement && renderQuestionPreview && (
                            <div className="c-themePreview__question">{renderQuestionPreview(questionPreviewElement)}</div>
                        )}

                        {elementPreview && (
                            <div style={{pointerEvents: 'none', ...previewFrameStyle(elementPreview)}}>
                                {renderThemeElement(elementPreview)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PresScaleProvider>
    );
}

export default ThemePreviewPanel;
